/* eslint-disable react-hooks/exhaustive-deps */
import { ComponentProps } from 'lib/component-props';
import { LinkField, useSitecoreContext } from '@sitecore-jss/sitecore-jss-nextjs';
import { useEffect, useState } from 'react';

import {
  CoveoEngineProvider,
  useCoveoEngine,
} from 'lib/utils/coveo/SearchContext/CoveoEngineContext';
import {
  buildSearchBox,
  loadAdvancedSearchQueryActions,
  buildResultList,
  buildInteractiveResult,
  buildSort,
  buildFieldSortCriterion,
  SortOrder,
  Result,
  InteractiveResult,
} from '@coveo/headless';

import PlainTextWrapper from 'helpers/SitecoreWrappers/PlainTextWrapper/PlainTextWrapper';
import RichTextWrapper from 'helpers/SitecoreWrappers/RichTextWrapper/RichTextWrapper';
import ImageWrapper from 'helpers/SitecoreWrappers/ImageWrapper/ImageWrapper';
import { PageSettings } from '../search/BlogSearch';
import Link from 'next/link';
import { SvgIcon } from 'helpers/SvgIcon';
import ComponentWrapper from 'helpers/SitecoreWrappers/ComponentWrapper/ComponentWrapper';
import { Search } from '.generated/Feature.CW.model';
import { Pages } from '.generated/Project.CW.model';
import { BLOG_TEMPLATE_ID } from 'lib/constants';
import { formatDate } from 'lib/utils/utilityFunction';

export type RelatedBlogsProps = ComponentProps &
  Search.RelatedBlogs & {
    contentType: string;
    isSidenavPart?: boolean;
    variant?: 'condensed' | 'expanded';
    params: {
      Theme: string;
    };
  };

const RelatedBlogs = (props: RelatedBlogsProps): JSX.Element => {
  let blogContentType = props?.rendering?.params?.blogContentType;
  const { sitecoreContext } = useSitecoreContext();
  const contextWithSettings = sitecoreContext?.siteSettings as PageSettings;

  if (sitecoreContext?.route?.templateId?.toLowerCase() === BLOG_TEMPLATE_ID.toLowerCase()) {
    const pageInfo = sitecoreContext?.route as Pages.BlogDetailPage;
    if (!blogContentType) {
      blogContentType = pageInfo?.fields?.blogContentType?.value;
    }
  }

  const [coveoConfig, setCoveoConfig] = useState({
    searchHub: '',
    pipeline: '',
    accessToken: '',
    organizationId: '',
  });

  useEffect(() => {
    setCoveoConfig({
      searchHub: contextWithSettings?.blogSRSearchHub?.value || '',
      pipeline: contextWithSettings?.blogSRPipeline?.value || '',
      accessToken: contextWithSettings?.blogSRAccessToken?.value || '',
      organizationId: process.env.NEXT_PUBLIC_COVEO_ORG_ID || '',
    });
  }, [contextWithSettings]);

  // Don't render if no blog content type or missing Coveo config
  if (
    !blogContentType ||
    !coveoConfig?.organizationId ||
    !coveoConfig?.accessToken ||
    !coveoConfig?.pipeline
  ) {
    return <></>;
  }

  return (
    <ComponentWrapper className="py-[80px] bg-theme-bg">
      <div
        className={`max-w-screen-xxl mx-auto  ${props?.isSidenavPart ? 'lg:px-0' : 'lg:px-[80px] px-10'}`}
      >
        <PlainTextWrapper
          field={props?.fields?.title}
          tag="h2"
          className="text-36 leading-[43px] font-bold font-centuryGothic text-gray-900 mb-[56px]"
        />

        <CoveoEngineProvider config={coveoConfig}>
          <RelatedBlogsResultList {...props} contentType={blogContentType} />
        </CoveoEngineProvider>
      </div>
    </ComponentWrapper>
  );
};

// Unified component that handles both condensed and expanded variants
export const RelatedBlogsUnified = (props: RelatedBlogsProps): JSX.Element => {
  let blogContentType = props?.rendering?.params?.blogContentType;
  const { sitecoreContext } = useSitecoreContext();
  const contextWithSettings = sitecoreContext?.siteSettings as PageSettings;

  if (sitecoreContext?.route?.templateId?.toLowerCase() === BLOG_TEMPLATE_ID.toLowerCase()) {
    const pageInfo = sitecoreContext?.route as Pages.BlogDetailPage;
    if (!blogContentType) {
      blogContentType = pageInfo?.fields?.blogContentType?.value;
    }
  }

  const [coveoConfig, setCoveoConfig] = useState({
    searchHub: '',
    pipeline: '',
    accessToken: '',
    organizationId: '',
  });

  useEffect(() => {
    setCoveoConfig({
      searchHub: contextWithSettings?.blogSRSearchHub?.value || '',
      pipeline: contextWithSettings?.blogSRPipeline?.value || '',
      accessToken: contextWithSettings?.blogSRAccessToken?.value || '',
      organizationId: process.env.NEXT_PUBLIC_COVEO_ORG_ID || '',
    });
  }, [contextWithSettings]);

  // Don't render if no blog content type or missing Coveo config
  if (
    !blogContentType ||
    !coveoConfig?.organizationId ||
    !coveoConfig?.accessToken ||
    !coveoConfig?.pipeline
  ) {
    return <></>;
  }

  return (
    <ComponentWrapper className="py-[80px] bg-theme-bg minal" params={props.params}>
      <div
        className={`max-w-screen-xxl mx-auto ${props?.isSidenavPart ? 'lg:px-0' : 'lg:px-[80px] px-10'}`}
      >
        <h2 className="!text-36 !leading-[43px] font-bold font-centuryGothic text-theme-text mb-[56px]">
          <PlainTextWrapper field={props?.fields?.title} />
        </h2>

        <CoveoEngineProvider config={coveoConfig}>
          <RelatedBlogsResultList {...props} contentType={blogContentType} />
        </CoveoEngineProvider>
      </div>
    </ComponentWrapper>
  );
};

// Convenience components for backward compatibility
export const CondensedVersion = (props: RelatedBlogsProps): JSX.Element => {
  return <RelatedBlogsUnified {...props} variant="condensed" />;
};

export const ExpandedVersion = (props: RelatedBlogsProps): JSX.Element => {
  return <RelatedBlogsUnified {...props} variant="expanded" />;
};

const RelatedBlogsResultList = ({
  contentType,
  variant = 'expanded',
  ...props
}: RelatedBlogsProps): JSX.Element => {
  const [results, setResults] = useState<Result[]>([]);
  const [totalResults, setTotalResults] = useState<number>(0);
  const blogDisplayCount = props?.rendering?.params?.blogDisplayCount;
  const count = (blogDisplayCount && parseInt(blogDisplayCount)) || 3;

  const { sitecoreContext } = useSitecoreContext();
  const contextWithSettings = sitecoreContext?.siteSettings as PageSettings;
  const blogSearchPage = contextWithSettings?.blogSearchPage as LinkField;

  const engine = useCoveoEngine();

  const fieldsToInclude = [
    'cwblogcontenttype',
    'cwtitle',
    'cwblogdate',
    'cwdescription',
    'cwauthorname',
    'cwauthortitle',
    'cwheroimage',
  ];

  // Create controllers with useState to persist across re-renders
  const [sortController] = useState(() =>
    engine
      ? buildSort(engine, {
          initialState: {
            criterion: buildFieldSortCriterion('cwblogdate', SortOrder.Descending),
          },
        })
      : null
  );

  const [resultListController] = useState(() =>
    engine
      ? buildResultList(engine, {
          options: {
            fieldsToInclude: fieldsToInclude,
          },
        })
      : null
  );

  const [searchBoxController] = useState(() =>
    engine
      ? buildSearchBox(engine, {
          options: { numberOfSuggestions: 0 },
        })
      : null
  );

  useEffect(() => {
    if (!engine || !contentType || !sortController || !resultListController || !searchBoxController)
      return;

    const performSearch = () => {
      try {
        // Set up advanced query to filter by content type
        const { updateAdvancedSearchQueries } = loadAdvancedSearchQueryActions(engine);
        const advancedQuery = `@cwblogcontenttype=="${contentType}"`;

        engine.dispatch(updateAdvancedSearchQueries({ aq: advancedQuery }));

        // Apply the sort criterion to ensure newest posts appear first
        sortController.sortBy(buildFieldSortCriterion('cwblogdate', SortOrder.Descending));

        // Subscribe to result list controller
        const unsubscribeResults = resultListController.subscribe(() => {
          const allResults = resultListController.state.results;
          setTotalResults(allResults.length);
          setResults(allResults.slice(0, count));
        });

        // Subscribe to sort controller to handle sort state changes
        const unsubscribeSort = sortController.subscribe(() => {
          // Sort state has changed, results will be updated via resultListController subscription
        });

        // Execute search with sorting applied
        searchBoxController.submit();

        // Cleanup function
        return () => {
          unsubscribeResults();
          unsubscribeSort();
        };
      } catch (error) {
        console.error('Error fetching related blogs:', error);
        return undefined;
      }
    };

    return performSearch();
  }, [engine, contentType, sortController, resultListController, searchBoxController, count]);

  if (results.length === 0) {
    return (
      <div className="text-center py-8">
        <p className="text-gray-500">No related articles found.</p>
      </div>
    );
  }

  return (
    <>
      <div
        className={`grid grid-cols-1  gap-[32px] ${!props?.isSidenavPart ? 'lg:grid-cols-3' : 'lg:grid-cols-2'}`}
      >
        {results.map((item: Result, index: number) => {
          const controller: InteractiveResult | null = engine
            ? buildInteractiveResult(engine, {
                options: { result: item },
              })
            : null;

          return (
            <article
              key={index}
              role="region"
              aria-labelledby={`${item?.raw?.cwtitle}`}
              className="border-b border-b-gray-50 last:border-0 lg:gap-[46px] gap-[24px] justify-between group shadow-default hover:shadow-hover rounded-md bg-white"
            >
              <div>
                <Link
                  aria-label={`${item?.raw?.cwtitle} blog title`}
                  href={item?.clickUri}
                  onClick={() => controller?.select()}
                  onMouseDown={() => controller?.select()}
                >
                  {' '}
                  <ImageWrapper
                    field={{
                      value: {
                        src: item?.raw?.cwheroimage as string,
                        alt: item?.raw?.cwtitle as string,
                      },
                    }}
                    className="w-full rounded-t-md"
                  />
                </Link>

                <div className="p-12">
                  <div className="flex w-full justify-between ">
                    <Link
                      aria-label={`${item?.raw?.cwblogcontenttype} blog content type`}
                      href={`${blogSearchPage?.value?.href}?blogContentTypeTag=${item?.raw?.cwblogcontenttype}`}
                    >
                      <PlainTextWrapper
                        field={{ value: item?.raw?.cwblogcontenttype as string }}
                        tag="p"
                        className="text-blue-base text-14 leading-22 font-bold font-centuryGothic pb-2 hover:text-darkblue-base"
                      />
                    </Link>
                    {variant === 'expanded' && (
                      <PlainTextWrapper
                        tag="p"
                        field={{ value: formatDate(item?.raw?.cwblogdate as number) }}
                        aria-label={`Published on ${formatDate(item?.raw?.cwblogdate as number)}`}
                        className="text-14 leading-22 font-nunitoSans text-gray-900"
                      />
                    )}
                  </div>
                  <div className="flex justify-between">
                    <Link
                      aria-label={`${item?.raw?.cwtitle} blog title`}
                      className="text-blue-base text-28 leading-31 font-bold font-centuryGothic block py-6 group-hover:underline group-hover:text-darkblue-base cursor-pointer flex-1"
                      href={item?.clickUri}
                      onClick={() => controller?.select()}
                      onMouseDown={() => controller?.select()}
                    >
                      <PlainTextWrapper field={{ value: item?.raw?.cwtitle as string }} tag="h3" />
                    </Link>
                    <SvgIcon
                      icon="arrow-right"
                      size="lg"
                      className="text-blue-base opacity-0 group-hover:opacity-100 transition-opacity shrink-0"
                    />
                  </div>

                  {item?.raw?.cwdescription !== undefined && (
                    <div className="text-gray-900 font-nunitoSans text-16 leading-25 font-[500] overflow-hidden line-clamp-2 max-w-full">
                      <RichTextWrapper
                        field={{
                          value:
                            (item?.raw?.cwdescription as string)?.substring(0, 200) +
                            ((item?.raw?.cwdescription as string)?.length > 200 ? '...' : ''),
                        }}
                        className="overflow-hidden"
                      />
                    </div>
                  )}

                  {variant === 'expanded' && (
                    <div className="flex gap-[8px] pt-6 pb-2 leading-22 text-14 text-[#111] font-nunitoSans">
                      <Link
                        href={`${blogSearchPage?.value?.href}?authorName=${item?.raw?.cwauthorname}`}
                        aria-label={`See more blog posts by ${item?.raw?.cwauthorname}`}
                        className="font-bold !text-blue-base hover:underline"
                      >
                        <PlainTextWrapper field={{ value: item?.raw?.cwauthorname as string }} />
                      </Link>

                      {item?.raw?.cwauthortitle !== undefined &&
                        item?.raw?.cwauthorname !== undefined && <span>|</span>}
                      <PlainTextWrapper field={{ value: item?.raw?.cwauthortitle as string }} />
                    </div>
                  )}
                </div>
              </div>
            </article>
          );
        })}
      </div>

      {/* Show "View More Stories" only if there are more results than displayed */}
      {totalResults > count && blogSearchPage && (
        <div className="mt-[32px] flex">
          <Link
            href={`${blogSearchPage?.value?.href}?blogContentTypeTag=${contentType}`}
            className="w-fit flex-row theme-text-btn border-theme-text-btn-border lg:flex font-bold font-centuryGothic gap-x-4 justify-center rounded-sm lg:text-center text-left !text-theme-text-btn-text active:bg-theme-text-btn-hover active:text-theme-text-btn-hover-text active:border-theme-text-btn-hover-border  focus:outline-offset-2 focus:outline-2 focus:outline-theme-text-btn-outline focus:border-theme-text-btn-border focus:outline hover:text-theme-text-btn-hover-text hover:underline hover:border-theme-text-btn-hover-border flex items-center"
          >
            <PlainTextWrapper field={props?.fields?.viewMoreCTA} tag="span" />
            <SvgIcon icon="arrow-right" size="md" className="text-theme-text-btn-text" />
          </Link>
        </div>
      )}
    </>
  );
};

export default RelatedBlogs;
